import {
  Alert,
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Grid, Show, VStack} from 'platform/foundation';

import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {
  useAssignVehicleWarrantyMutation,
  useListVehicleWarrantiesQuery,
  useListWarrantyTypeQuery,
  useUpdateVehicleWarrantyMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {
  getApiDateTimeString,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
  useNavigate,
} from 'shared/web';

import {
  VehicleWarrantyFormSchema,
  VehicleWarrantyFormType,
} from '../schemas/VehicleWarrantyFormSchema';

interface VehicleWarrantyFormProps extends RequiredTestIdProps {
  vehicleId: string;
  // When set, the form edits an existing assignment; the product cannot be changed.
  warrantyProductId?: string;
  onSuccess: () => void;
  onDiscard: () => void;
}

export function VehicleWarrantyForm(props: VehicleWarrantyFormProps) {
  const navigate = useNavigate();

  const isEditing = isNotNil(props.warrantyProductId);

  const {data: warrantyTypes, isLoading: areWarrantyTypesLoading} = useListWarrantyTypeQuery();

  const {data: vehicleWarranties, isLoading: areVehicleWarrantiesLoading} =
    useListVehicleWarrantiesQuery({vehicleId: props.vehicleId});

  const assignedWarranty = vehicleWarranties?.find(
    (item) => item.warrantyProductId === props.warrantyProductId
  );

  const [assignVehicleWarranty] = useAssignVehicleWarrantyMutation();
  const [updateVehicleWarranty] = useUpdateVehicleWarrantyMutation();

  // A warranty product can be assigned to a vehicle only once — offer only unused products,
  // keeping the currently edited one so the disabled field can render its label.
  const usedWarrantyProductIds = vehicleWarranties?.map((item) => item.warrantyProductId) ?? [];

  const warrantyProductOptions =
    warrantyTypes
      ?.filter(
        (warrantyType) =>
          warrantyType.id === props.warrantyProductId ||
          !usedWarrantyProductIds.includes(warrantyType.id)
      )
      .map((warrantyType) => ({
        label: warrantyType.description,
        value: warrantyType.id,
      })) ?? [];

  const hasNoAvailableWarrantyTypes =
    !isEditing &&
    !areWarrantyTypesLoading &&
    !areVehicleWarrantiesLoading &&
    isNilOrEmpty(warrantyProductOptions);

  const handleGoToWarrantyTypeCreation = () => {
    closeCurrentDialog();
    navigate(settingsRoutes.warrantyTypesCreate);
  };

  const handleSubmit: FormSubmitHandler<VehicleWarrantyFormType> = async (data) => {
    // The API expects a full date-time; the date picker yields a Date.
    const startDate = getApiDateTimeString(data.startDate);
    const endDate = isNotNil(data.endDate) ? getApiDateTimeString(data.endDate) : null;

    if (isNotNil(props.warrantyProductId)) {
      await updateVehicleWarranty({
        vehicleId: props.vehicleId,
        warrantyProductId: props.warrantyProductId,
        updateWarrantyAssignmentRequestBody: {
          startDate,
          endDate,
          startOdometerKm: data.startOdometerKm,
          endOdometerKm: data.endOdometerKm,
        },
      })
        .unwrap()
        .then(props.onSuccess)
        .catch(handleApiError);
      return;
    }

    await assignVehicleWarranty({
      vehicleId: props.vehicleId,
      assignWarrantyProductRequestBody: {
        warrantyProductId: data.warrantyProductId,
        startDate,
        endDate,
        startOdometerKm: data.startOdometerKm,
        endOdometerKm: data.endOdometerKm,
        isReadOnly: false,
      },
    })
      .unwrap()
      .then(props.onSuccess)
      .catch(handleApiError);
  };

  const defaultValues = {
    warrantyProductId: props.warrantyProductId,
    startDate: assignedWarranty ? parseDate(assignedWarranty.startDate) : undefined,
    endDate: assignedWarranty?.endDate ? parseDate(assignedWarranty.endDate) : null,
    startOdometerKm: assignedWarranty?.startOdometerKm ?? null,
    endOdometerKm: assignedWarranty?.endOdometerKm ?? null,
  };

  return (
    <DataStatus isLoading={isEditing && areVehicleWarrantiesLoading} minHeight={60}>
      <Form<VehicleWarrantyFormType>
        defaultValues={defaultValues}
        experimentalZodSchema={VehicleWarrantyFormSchema}
        onSubmit={handleSubmit}
      >
        {(control) => (
          <VStack spacing={4}>
            <Show when={hasNoAvailableWarrantyTypes}>
              <Alert
                variant="info"
                title={i18n.t('entity.vehicleWarranty.notifications.allWarrantyTypesUsed')}
                hyperlinks={[
                  {
                    title: i18n.t('entity.warrantyType.actions.createNewWarrantyType'),
                    leftIcon: 'navigation/arrow_forward',
                    onClick: handleGoToWarrantyTypeCreation,
                  },
                ]}
                data-testid={suffixTestId('allWarrantyTypesUsed', props)}
              />
            </Show>
            <FormField
              control={control}
              type="choice"
              name="warrantyProductId"
              label={i18n.t('entity.warrantyType.labels.warrantyType')}
              options={warrantyProductOptions}
              isLoading={areWarrantyTypesLoading || areVehicleWarrantiesLoading}
              isRequired
              isDisabled={isEditing || hasNoAvailableWarrantyTypes}
              data-testid={suffixTestId('warrantyProduct', props)}
            />
            <Grid columns={2} spacing={4}>
              <FormField
                control={control}
                type="date"
                name="startDate"
                label={i18n.t('entity.vehicle.labels.startDate')}
                isRequired
                data-testid={suffixTestId('startDate', props)}
              />
              <FormField
                control={control}
                type="date"
                name="endDate"
                label={i18n.t('entity.vehicle.labels.endDate')}
                data-testid={suffixTestId('endDate', props)}
              />
              <FormField
                control={control}
                type="integer"
                name="startOdometerKm"
                label={i18n.t('entity.vehicleWarranty.labels.startOdometer')}
                suffix={i18n.t('general.metric.km')}
                data-testid={suffixTestId('startOdometer', props)}
              />
              <FormField
                control={control}
                type="integer"
                name="endOdometerKm"
                label={i18n.t('entity.vehicleWarranty.labels.endOdometer')}
                suffix={i18n.t('general.metric.km')}
                data-testid={suffixTestId('endOdometer', props)}
              />
            </Grid>
            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.discard')}
                variant="secondary"
                onClick={props.onDiscard}
                data-testid={suffixTestId('discard', props)}
              />
              <FormButton
                control={control}
                title={i18n.t('general.actions.save')}
                type="submit"
                variant="primary"
                isDisabled={hasNoAvailableWarrantyTypes}
                data-testid={suffixTestId('submit', props)}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
